import React,{ useCallback,useEffect,useMemo,useRef,useState } from 'react';
import api from '../../config/api';
import { productLabelForHostname } from '../../utils/portalExperience';
import CmaInfoConnectionPanel from './CmaInfoConnectionPanel';

const endpoint='/api/cma-info/report';
const button={ padding:'9px 14px',border:'1px solid #11575C',borderRadius:7,background:'#fff',color:'#11575C',fontWeight:700,cursor:'pointer' };

// Availability is metadata only. Every lookup/retrieval requires its own
// deliberate click; changing source, opening or remounting never buys data.
// The owner of this hook stays mounted while its controls are docked. Keeping
// the request, selection grants and result here lets reopening review the same
// work without starting another provider request.
export function useCmaInfoReportController({ userId,leadId,propertyId='',query='',scopeKey='',enabled=true,onResult,onAvailability,onBusyChange }) {
    const [availability,setAvailability]=useState(null);
    const [matches,setMatches]=useState([]);
    const [busy,setBusy]=useState(false);
    const [message,setMessage]=useState('Checking CMA Info availability…');
    const [gaps,setGaps]=useState([]);
    const [status,setStatus]=useState('idle');
    const [result,setResult]=useState(null);
    const [resultQuery,setResultQuery]=useState(null);
    const [refresh,setRefresh]=useState(0);
    const [billingApproval,setBillingApproval]=useState(null);
    const generation=useRef(0);
    const requestGeneration=useRef(0);
    const inFlight=useRef(null);
    const currentQuery=useRef(query);
    const callbacks=useRef({ onResult,onAvailability,onBusyChange });
    callbacks.current={ onResult,onAvailability,onBusyChange };
    const context=JSON.stringify([String(userId || ''),String(leadId || ''),String(propertyId || ''),scopeKey]);
    const billingContext=JSON.stringify([context,query,availability?.connection?.connectionId,availability?.connection?.revision]);
    const confirmClientBilling=billingApproval===billingContext;
    const setConfirmClientBilling=useCallback(value => setBillingApproval(value ? billingContext : null),[billingContext]);
    const active=useRef(null);
    active.current={ context,query,enabled };

    useEffect(() => {
        const version=++generation.current;
        requestGeneration.current+=1;
        setAvailability(null);setMatches([]);setGaps([]);setBusy(false);setStatus('idle');setResult(null);setResultQuery(null);
        setBillingApproval(null);
        callbacks.current.onAvailability?.(false);callbacks.current.onBusyChange?.(false);
        if (!enabled) return undefined;
        if (!userId || !leadId) { setMessage('Choose an existing CRM client before using CMA Info.');return undefined; }
        setMessage('Checking CMA Info availability…');
        api.get(`${endpoint}?${new URLSearchParams({ leadId })}`)
            .then(({ data }) => {
                if (generation.current!==version || active.current.context!==context || !active.current.enabled) return;
                setAvailability(data);callbacks.current.onAvailability?.(data?.available===true);
                setMessage(data?.available===true ? 'CMA Info is available for this client. Search and confirm the exact property before retrieving.' : data?.message || 'CMA Info is not available for this account yet.');
            }).catch(error => {
                if (generation.current===version) setMessage(error.response?.data?.message || 'CMA Info availability could not be confirmed. No lookup was started.');
            });
        return () => { generation.current+=1; };
    // Context is a stable identity; the individual primitive props are part of it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[context,refresh,enabled]);
    useEffect(() => {
        if (currentQuery.current!==query) {
            currentQuery.current=query;setMatches([]);setResult(null);setResultQuery(null);setStatus('idle');setBillingApproval(null);requestGeneration.current+=1;
        }
    },[query]);

    const request=useCallback(async (action,selectionToken) => {
        if (!enabled || inFlight.current?.context===context || !leadId || (action!=='reuse' && availability?.available!==true)) return;
        if (action!=='reuse' && !confirmClientBilling) { setMessage('Confirm your authorisation to use this client’s CMA Info credits before starting a new lookup.');return; }
        if (action==='suggest' && !query.trim()) { setMessage('Enter the property street address before searching.');return; }
        const version=++requestGeneration.current;
        const scopeVersion=generation.current;
        // React batches state changes: the synchronous lock closes the window
        // where a second click could purchase another lookup before busy paints.
        const lock={ context,version };
        inFlight.current=lock;
        const isCurrent=() => active.current.enabled && active.current.context===context && active.current.query===query
            && generation.current===scopeVersion && requestGeneration.current===version;
        setBusy(true);setStatus(action==='suggest' ? 'searching' : 'retrieving');
        callbacks.current.onBusyChange?.(true);setMessage(action==='suggest' ? 'Finding matching CMA Info properties…' : 'Reading the selected CMA Info property…');
        if (action==='suggest') setMatches([]);
        try {
            const body={ provider:'cma-info',leadId,action };
            if (action==='suggest') Object.assign(body,{ query:query.trim(),confirmLookup:true,confirmClientBilling:true });
            else if (action==='retrieve') Object.assign(body,{ selectionToken,confirmProperty:true,confirmLookup:true,confirmClientBilling:true });
            else body.snapshotId=availability.existingSnapshot.id;
            const { data }=await api.post(endpoint,body,{ timeout:180000 });
            if (!isCurrent()) return;
            if (!data?.ok) throw Error(data?.message || 'CMA Info did not return usable property data.');
            if (action==='suggest') {
                const next=Array.isArray(data.nearby) ? data.nearby.filter(row => row.selectionToken && row.address) : [];
                setMatches(next);setStatus(next.length ? 'confirm' : 'idle');setMessage(data.truncated ? 'These are the matches found so far. Refine the address or locality if your exact property is not listed, then confirm the property below.' : next.length ? 'Confirm the exact property below to retrieve its CMA data.' : 'No exact property was found. Check the address before another search.');
            } else {
                setMatches([]);setStatus('done');setResult(data);setResultQuery(query);callbacks.current.onResult?.(data);
                setGaps(Object.entries(data.sections || {}).filter(([,section]) => section?.status==='unavailable' || section?.status==='warning')
                    .map(([name,section]) => section.message || (name==='storage' ? 'The details are in your report, but could not be saved for reuse. Check the report before another lookup.' : `CMA Info did not return ${name} details for this property.`)));
                if (data.sourceSnapshot?.id) setAvailability(previous => ({ ...previous,existingSnapshot:data.sourceSnapshot }));
                setMessage(productLabelForHostname('CMA Info details are ready in your report. Use Print / PDF to create the branded IPM report.'));
            }
        } catch (error) {
            if (!isCurrent()) return;
            setStatus('error');
            setMessage(error.response?.data?.message || error.message || 'The lookup did not finish. Check existing results before retrying.');
            // Selection grants are short-lived. A failed retrieve must not be
            // replayed automatically after an uncertain provider response.
            if (action==='retrieve') setMatches([]);
        } finally {
            // A stale account request must never unlock the new account's work.
            // Query edits invalidate results but retain this lock until it ends.
            if (inFlight.current===lock) {
                inFlight.current=null;
                if (generation.current===scopeVersion) { setBusy(false);callbacks.current.onBusyChange?.(false); }
            }
        }
    },[enabled,context,leadId,query,availability,confirmClientBilling]);
    const refreshAvailability=useCallback(() => setRefresh(value => value+1),[]);
    return useMemo(() => ({ availability,matches,busy,message,gaps,status,result,resultQuery,request,refresh:refreshAvailability,
        confirmClientBilling,setConfirmClientBilling,connectionContext:context,connectionEnabled:enabled && Boolean(userId && leadId) }),
        [availability,matches,busy,message,gaps,status,result,resultQuery,request,refreshAvailability,confirmClientBilling,setConfirmClientBilling,context,enabled,userId,leadId]);
}

export function CmaInfoReportControls({ controller }) {
    const { availability,matches,busy,message,gaps,request,refresh,confirmClientBilling,setConfirmClientBilling,connectionContext,connectionEnabled }=controller;
    const [connectionBusy,setConnectionBusy]=useState(false);
    const blocked=busy || connectionBusy;
    return <section aria-label="CMA Info property data" style={{ border:'1px solid #bed5cc',borderRadius:10,padding:14,marginBottom:16,background:'#f2f8f4' }}>
        <strong>Property report source: CMA Info</strong>
        <p role="status" style={{ fontSize:12,lineHeight:1.5 }}>{message}</p>
        {gaps.length>0 && <ul role="note" aria-label="CMA Info report gaps" style={{ fontSize:12 }}>{gaps.map((gap,index) => <li key={index}>{gap}</li>)}</ul>}
        <p style={{ fontSize:12 }}>New lookups use this client’s own CMA Info vendor account and credits for property reports only. Contacts stay with Virtual Agent; WinDeed is separate. No Lightstone request is used as a fallback.</p>
        {availability?.available===true && <label style={{display:'block',fontSize:12,marginBottom:10}}><input type="checkbox" checked={confirmClientBilling} disabled={blocked} onChange={event => setConfirmClientBilling(event.target.checked)} /> I am authorised to use this client’s CMA Info credits for this property lookup.</label>}
        <div style={{ display:'flex',flexWrap:'wrap',gap:8 }}>
            <button type="button" style={button} disabled={blocked || availability?.available!==true || !confirmClientBilling} onClick={() => request('suggest')}>Find matching CMA Info properties</button>
            {availability?.existingSnapshot?.id && <button type="button" style={button} disabled={blocked} onClick={() => request('reuse')}>Reuse saved CMA Info data</button>}
            {!availability?.available && <button type="button" style={button} disabled={blocked} onClick={refresh}>Check CMA Info availability</button>}
        </div>
        {matches.length>0 && <ul>{matches.map((match,index) => <li key={match.selectionToken} style={{ marginTop:10 }}>
            <span>{match.address}{match.suburb ? ` · ${match.suburb}` : ''}</span>{' '}
            <button type="button" style={button} disabled={blocked || !confirmClientBilling} onClick={() => request('retrieve',match.selectionToken)}>Confirm property {index+1} and retrieve CMA data</button>
        </li>)}</ul>}
        <CmaInfoConnectionPanel key={connectionContext} contextKey={connectionContext} enabled={connectionEnabled===true} locked={busy} onSaved={refresh} onBusyChange={setConnectionBusy} />
    </section>;
}

export default function CmaInfoReportPanel(props) {
    const controller=useCmaInfoReportController(props);
    return <CmaInfoReportControls controller={controller} />;
}
